/**
 * PipelineDashboard Page
 *
 * Main dashboard for Pipeline Orchestrator.
 * Shows pipeline runs, progress, pilot review, and loop detection.
 *
 * Part of Story 54: Automated AEO Pipeline with Pilot Mode and Impact Propagation
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { PipelineRunList } from '@/components/pipeline/PipelineRunList';
import { PipelineProgress } from '@/components/pipeline/PipelineProgress';
import { PilotReview } from '@/components/pipeline/PilotReview';
import { LoopAlerts } from '@/components/pipeline/LoopAlerts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function PipelineDashboard() {
  const { selectedSite } = useSite();
  const [searchParams] = useSearchParams();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('progress');

  // Handle URL params for returning from enrichment-review
  useEffect(() => {
    const runId = searchParams.get('runId');
    if (runId) {
      setSelectedRunId(runId);
      // Switch to loops tab since they're likely returning from fixing a looped product
      setActiveTab('loops');
    }
  }, [searchParams]);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site from the header to view pipeline runs.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">AEO Pipeline</h1>
        <p className="text-muted-foreground">
          Automated content generation with validation and research loops
        </p>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Run List */}
        <div className="col-span-4">
          <PipelineRunList
            key={`list-${refreshKey}`}
            siteId={selectedSite.id}
            selectedRunId={selectedRunId || undefined}
            onSelectRun={(runId) => setSelectedRunId(runId)}
          />
        </div>

        {/* Right Column - Details */}
        <div className="col-span-8">
          {selectedRunId ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList>
                <TabsTrigger value="progress">Progress</TabsTrigger>
                <TabsTrigger value="pilot">Pilot Review</TabsTrigger>
                <TabsTrigger value="loops">Loop Alerts</TabsTrigger>
              </TabsList>

              <TabsContent value="progress" className="space-y-4">
                <PipelineProgress
                  key={`progress-${selectedRunId}-${refreshKey}`}
                  runId={selectedRunId}
                  onRefresh={handleRefresh}
                  onTabChange={handleTabChange}
                />
              </TabsContent>

              <TabsContent value="pilot" className="space-y-4">
                <PilotReview
                  key={`pilot-${selectedRunId}-${refreshKey}`}
                  runId={selectedRunId}
                />
              </TabsContent>

              <TabsContent value="loops" className="space-y-4">
                <LoopAlerts
                  key={`loops-${selectedRunId}-${refreshKey}`}
                  runId={selectedRunId}
                  siteId={selectedSite.id}
                  onAction={handleRefresh}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-muted/20">
              <div className="text-center text-muted-foreground">
                <p className="text-lg font-medium">No pipeline selected</p>
                <p className="text-sm mt-1">
                  Select a pipeline run from the list or create a new one
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
